import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  DatePicker,
  FormLayout,
  FormRow,
  Input,
  Typography,
} from '@breeze-ai/ui-library';
import { Button } from '@breezeai-frontend/cargo-ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import moment from 'moment';

import { useAppConfiguration } from '../../../../context/app-configuration/useAppConfiguration';
import { useUser, useUserSetting } from '../../../../context/auth/auth-hooks';
import { usePlatform } from '../../../../context/PlatformContext';
import { type Place, QueryLevelEnum } from '../../../../model/Place';
import { type Quote } from '../../../../model/Quote';
import { IncotermSelector } from '../../../components/IncotermSelector';
import { PlaceAutocomplete } from '../../../components/PlaceAutocomplete/PlaceAutocomplete';
import { PolicySummary } from '../../../components/PolicySummary/PolicySummary';
import { useTranslationContext } from '../../../hooks/useTranslationContext';
import { policiesQueries } from '../../../policies/network/queries';
import {
  getArrivalDateLimits,
  getDepartureDateLimits,
} from '../../../utils/eta-etd';
import { FlowSteps } from '../../constants';
import {
  useInputValidation,
  usePolicyFlowContext,
  useSetFormData,
  useSetFormError,
} from '../../hooks/context-hooks';
import { useShipmentInformationForm } from '../../hooks/use-shipment-information-form';
import { usePolicyCtaText } from '../hooks/usePolicyCtaText';
import { useShipmentFormLabels } from './useShipmentFormLabels';

type ShipmentInformationFormProps = {
  quote: Quote;
  onSubmit: () => void;
};

export const ShipmentInformationForm = ({
  quote,
  onSubmit,
}: ShipmentInformationFormProps) => {
  const { platform, isBreeze, isWtw } = usePlatform();
  const { t } = useTranslation();
  const tContext = useTranslationContext();

  const user = useUser();

  const etdMaxDate = () => {
    if (isBreeze) {
      return etdLimits.max;
    }
    if (user?.distributor.gcid === '2534830') {
      // TODO HACK [2025-06-15]: This is a temporary fix for HJ Baker. See: https://breeze.fibery.io/Breeze/Daily-66#Task/HJ-Baker-termination-date,-departure-date-lock-to-May-6th-3932
      return moment('2025-05-05');
    }

    return undefined;
  };

  const { incoterms } = useAppConfiguration();
  const etdBackdateToleranceDays = useUserSetting<number>(
    'etd_backdate_tolerance_days',
  );
  const enableShowPremiumInPdf = useUserSetting<boolean>(
    'enable_show_premiums_in_pdf',
  );
  const defaultShowPremiumInPdf = useUserSetting<boolean>(
    'default_show_premiums_in_pdf',
  );

  const { step } = usePolicyFlowContext();

  const { data: configuration } = useSuspenseQuery(
    policiesQueries.policyFormConfiguration(),
  );
  const {
    data: { coverage_options },
  } = useSuspenseQuery(policiesQueries.policyFormConfiguration());

  const { data, isValid, errors } = useShipmentInformationForm(configuration);
  const setFormData = useSetFormData('shipment-info');
  const setFormError = useSetFormError();
  const validateInput = useInputValidation();

  const ctaText = usePolicyCtaText(FlowSteps.SHIPMENT_INFORMATION);

  useEffect(() => {
    if (isBreeze) return;

    setFormData({
      issueDate: moment(),
      showPremiumInPdf: enableShowPremiumInPdf
        ? defaultShowPremiumInPdf
        : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shipmentFormLabels = useShipmentFormLabels(
    quote.primary_transport_mode_code,
  );
  const { etd, eta, issueDate, containerIds, incoterm, customer } = data;

  const { company_name, address, email } = customer ?? {};

  const enableReferPolicyFlow = Boolean(
    user?.settings.find((s) => s.name === 'enable_refer_a_policy_flow')?.value,
  );

  const etdLimits = getDepartureDateLimits({
    eta,
    terminationDate: moment(quote.open_cover?.termination_date),
    etdBackdateToleranceDays,
  });
  const etaLimits = getArrivalDateLimits({
    etd,
    inceptionDate: isBreeze
      ? moment(quote.open_cover?.inception_date)
      : undefined,
    terminationDate: isBreeze
      ? moment(quote.open_cover?.termination_date)
      : undefined,
  });

  const issueText = t('form.policyForm.TitleIssue', {
    ns: 'quotes',
    context: platform,
  });
  const referText = t('form.policyForm.TitleRefer', {
    ns: 'quotes',
    context: platform,
  });
  const extraReferralText = t('form.policyForm.ExtraReferralText', {
    ns: 'quotes',
    context: platform,
  });

  return (
    <div
      className="flex flex-col items-center py-7 px-11 bg-white rounded-[30px] mt-5"
      data-testid="shipment-information-form"
    >
      <Typography level="h2">
        {enableReferPolicyFlow ? referText : issueText}
        {enableReferPolicyFlow ? '.' : ' '}
        <br />
        {enableReferPolicyFlow && extraReferralText}
      </Typography>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-x-8 gap-y-12 xl:gap-y-0 mt-12 text-pretty">
        <FormLayout className="col-span-2">
          <FormRow>
            <Typography level="h3">
              {t('CargoOwnerDetails', {
                ns: 'common',
                context: tContext,
              })}
            </Typography>
          </FormRow>
          <FormRow>
            <Input
              label={t('CargoOwnerName', {
                ns: 'common',
                context: tContext,
              })}
              testId="customer-name-input"
              value={company_name}
              disabled={!!company_name}
              onChange={({ target: { value: company_name } }) =>
                setFormData({ customer: { company_name } })
              }
              error={errors?.customer?.company_name?.error}
              errorHelperText={errors?.customer?.company_name?.reason}
              validator={(value) =>
                validateInput('customer.company_name', value)
              }
            />
            <PlaceAutocomplete
              value={address as Place}
              suggestionsQueryLevel={QueryLevelEnum.STREET}
              inputProps={{
                label: t('Address', {
                  ns: 'common',
                  context: platform,
                }),
                placeholder: 'Cargo owner address',
                testId: 'customer-address-autocomplete',
                required: configuration?.is_address_required,
                error: errors?.customer?.address?.error,
                errorHelperText: errors?.customer?.address?.reason,
                validator: (value) => {
                  // If the value is empty, we don't need to validate the address
                  if (value.length === 0) {
                    setFormError({
                      field: 'customer.address',
                      error: false,
                      reason: '',
                    });
                    return true;
                  }
                  if (address?.address?.full_address !== value) {
                    setFormError({
                      field: 'customer.address',
                      error: true,
                      reason:
                        'Type to search and select a precise address from the list',
                    });
                    return true;
                  }
                  // These return is required by the PlaceAutocomplete component but has no effect on the validation
                  return true;
                },
              }}
              onChange={(place) => {
                setFormData({ customer: { address: place } });
                // Clear the error if the address is changed
                setFormError({
                  field: 'customer.address',
                  error: false,
                  reason: '',
                });
              }}
              getOptionLabel={(value: Place) =>
                value?.address?.full_address ?? value.name ?? ''
              }
            />
          </FormRow>
          {enableReferPolicyFlow && (
            <FormRow className="col-span-1">
              <Input
                label="Cargo Owner Email"
                testId="customer-email-input"
                value={email}
                onChange={({ target: { value: email } }) =>
                  setFormData({ customer: { email } })
                }
                required={enableReferPolicyFlow}
                error={errors?.customer?.email?.error}
                errorHelperText={errors?.customer?.email?.reason}
                validator={(value) =>
                  validateInput('customer.email', value, {
                    required: enableReferPolicyFlow,
                  })
                }
              />
            </FormRow>
          )}
          <FormRow>
            <Typography level="h3">
              {t('ShipmentDetails', {
                ns: 'common',
                context: platform,
              })}
            </Typography>
          </FormRow>

          <FormRow>
            {isWtw && (
              <DatePicker
                value={issueDate}
                onChange={(issueDate) =>
                  setFormData({ issueDate: issueDate ?? undefined })
                }
                onValidation={(error, reason) =>
                  setFormError({ field: 'issueDate', error, reason })
                }
                testId="issue-date-picker"
                required={true}
                inputProps={{
                  label: t('IssueDate', {
                    ns: 'common',
                    context: platform,
                  }),
                  error: errors?.issueDate?.error,
                  errorHelperText: errors?.issueDate?.reason,
                }}
              />
            )}
            <DatePicker
              disabled={isWtw}
              value={etd}
              // Date is already populated and field is disabled for WTW, so limits are not needed
              minDate={isWtw ? undefined : etdLimits.min}
              maxDate={etdMaxDate()}
              onChange={(etd) => setFormData({ etd: etd ?? undefined })}
              onValidation={(error, reason) =>
                setFormError({ field: 'etd', error, reason })
              }
              testId="etd-date-picker"
              required={true}
              inputProps={{
                testId: 'etd-date-picker-input',
                label: t('DateOfDeparture', {
                  ns: 'common',
                  context: tContext,
                }),
                error: errors?.etd?.error,
                errorHelperText: errors?.etd?.reason,
              }}
            />
            <DatePicker
              value={eta}
              minDate={etaLimits.min}
              maxDate={etaLimits.max}
              onChange={(eta) => setFormData({ eta: eta ?? undefined })}
              onValidation={(error, reason) =>
                setFormError({ field: 'eta', error, reason })
              }
              testId="eta-date-picker"
              required={isWtw ? false : true}
              inputProps={{
                testId: 'eta-date-picker-input',
                label: t('DateOfArrival', {
                  ns: 'common',
                  context: platform,
                }),
                error: errors?.eta?.error,
                errorHelperText: errors?.eta?.reason,
              }}
            />
          </FormRow>
          <FormRow style={{ width: isWtw ? '50%' : '100%' }}>
            <Input
              defaultValue={quote.external_reference ?? ''}
              disabled={!!quote.external_reference}
              label={t('BookingReference', {
                ns: 'common',
                context: platform,
              })}
              placeholder={t('BookingReferencePlaceholder', {
                ns: 'common',
                context: platform,
              })}
              testId="shipment-information-booking-reference-input"
              onChange={({ target: { value: externalReference } }) =>
                setFormData({ externalReference })
              }
              error={errors?.externalReference?.error}
              errorHelperText={errors?.externalReference?.reason}
              validator={(value) =>
                validateInput('externalReference', value, {
                  required: configuration?.is_shipment_id_required,
                })
              }
            />
            {!isWtw && (
              <Input
                value={containerIds ?? ''}
                label={shipmentFormLabels?.containerIds}
                placeholder="Number1, number2,..."
                onChange={({ target: { value: containerIds } }) =>
                  setFormData({ containerIds })
                }
                testId="container-ids-input"
                error={errors?.containerIds?.error}
                required={configuration?.is_container_numbers_required}
                helperText="Insert comma-separated numbers"
                errorHelperText={errors?.containerIds?.reason}
                validator={(value) =>
                  validateInput('containerIds', value, {
                    required: configuration?.is_container_numbers_required,
                  })
                }
              />
            )}
          </FormRow>
          {incoterms.length > 0 && (
            // TODO HACK: have to use style due to FormRow implementation
            <FormRow style={{ width: '50%' }}>
              <IncotermSelector
                options={incoterms}
                selectedIncoterm={incoterm}
                onChange={(incoterm) => setFormData({ incoterm })}
              />
            </FormRow>
          )}
          {enableShowPremiumInPdf && (
            <FormRow>
              <Checkbox
                data-testid="show-premium-in-pdf-checkbox"
                defaultChecked={defaultShowPremiumInPdf}
                aria-label="Show premium in certificate"
                onChange={(value) => setFormData({ showPremiumInPdf: value })}
                label={t('ShowPremium', {
                  ns: 'common',
                  context: platform,
                })}
              />
            </FormRow>
          )}
          <FormRow align="end" className="mt-auto">
            <Button
              variant="primary"
              data-testid="shipment-information-form-submit-button"
              onPress={onSubmit}
              isDisabled={!isValid || step === FlowSteps.LOADING}
              label={ctaText}
            />
          </FormRow>
        </FormLayout>
        <div className="row-start-1 xl:row-auto">
          <PolicySummary quote={quote} coverageOptions={coverage_options} />
        </div>
      </div>
    </div>
  );
};
